import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, CardHeader, Divider, IconButton, Typography } from '@mui/material';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { preventDefault, useBox } from 'shared/hooks/utils';
import { Author } from 'z3k-config';
import { unpack_validation } from 'z3k-parser/validation/types';
import * as React from 'react';
import { useCallback, useContext, useMemo, useState, } from 'react';
import { Z3KSettings } from '../../../provider/types';
import { isAuthor } from 'z3k-parser/model/author';
import { MaterialHelpPopupUncontrolled } from '../help-popup';
import { SettingCard } from '../settings-card/card';
import { SettingFullRow } from '../settings-card/full-row';
import { AccordionWithValidation } from '../accordion-with-validation';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { z3kSettingsUpdateOpenedIds } from 'z3k-settings/redux/actions';
import { useListEditingCallbacks } from '../../utils/drag-drop-drawer/config';
import { useBooleanState } from 'shared/hooks/state/boolean';
export function Z3kAccordonList(props) {
    const { service_view, device_id } = useContext(Z3KSettings);
    const [msgs, setMsgs] = useState([]);
    const [going_to_delete_object, setGoingToDeleteObject] = useState(null);
    const { help_id, mainTitleComponentRender, objectFilter, beforeTitleComponentRender, nameGetter, titleComponentRender, } = props;
    const { value: list_of_objects, validation_result, validation_warnings_result, onAddClick, onDelete, onChange, onConfirmDelete, } = useListEditingCallbacks(props.z3k_name);
    const onDeleteFunc = props.onDelete ?? onDelete;
    const onClickAddFunc = props.onAddClick ?? onAddClick;
    const onConfirmDeleteFunc = props.onConfirmDelete ?? onConfirmDelete;
    const opened_ids = useAppSelector((state) => device_id != null ? state.z3k_settings.openedIds ?? [] : []);
    const Component = props?.object;
    const onUpdateOpenedIds = useAction(z3kSettingsUpdateOpenedIds);
    const boxed_list_of_objects = useBox(list_of_objects);
    const [dialog_open, closeDialog, openDialog] = useBooleanState(false);
    const onClickAddButton = useCallback(() => {
        onClickAddFunc?.();
    }, [onAddClick]);
    const hideAddButton = useMemo(() => props.hideAddButton ?? !service_view, [props.hideAddButton, service_view]);
    const compareFn = useCallback(([a], [b]) => {
        const ap = 'position' in a ? a.position ?? 0 : 0, bp = 'position' in b ? b.position ?? 0 : 0;
        if (ap === 0 && bp === 0)
            return 0;
        if (ap === 0)
            return 1;
        if (bp === 0)
            return -1;
        return ap - bp;
    }, []);
    const onDeleteObject = useCallback((object) => {
        const new_list = boxed_list_of_objects.current.filter((obj) => obj.id !== object.id);
        if (onDeleteFunc != null) {
            onDeleteFunc(object, new_list);
        }
        else {
            onChange(new_list);
        }
    }, [onDeleteFunc, onChange]);
    const onTrashClick = useCallback((object) => (ev) => {
        preventDefault(ev);
        const new_list = boxed_list_of_objects.current.filter((obj) => obj.id !== object.id);
        const error_msgs = onConfirmDeleteFunc?.(object, new_list) ?? null;
        if (error_msgs && error_msgs.length > 0) {
            openDialog();
            setMsgs(error_msgs);
            setGoingToDeleteObject(object);
        }
        else {
            onDeleteObject(object);
        }
    }, [
        onConfirmDeleteFunc,
        setMsgs,
        setGoingToDeleteObject,
        onDeleteObject,
        openDialog,
    ]);
    const onClickCloseDialog = useCallback(() => {
        closeDialog();
        setGoingToDeleteObject(null);
    }, [closeDialog, setGoingToDeleteObject]);
    const onClickDeleteDialog = useCallback(() => {
        onClickCloseDialog();
        if (going_to_delete_object != null) {
            onDeleteObject(going_to_delete_object);
        }
    }, [onClickCloseDialog, onDeleteObject, going_to_delete_object]);
    const showTrashButton = useCallback((object) => {
        if (props.hideTrashButton ?? !service_view) {
            return false;
        }
        return !isAuthor(object.id, Author.PREDEFINED);
    }, [props.hideTrashButton, service_view]);
    const panels = useMemo(() => {
        const onTitleClick = (panel_id) => (ev) => {
            preventDefault(ev);
            const all_possible_object_ids = (boxed_list_of_objects.current ?? []).map((obj) => obj.id);
            let new_opened_ids = opened_ids.filter(
            //@ts-ignore
            (id) => !all_possible_object_ids.includes(id));
            if (!opened_ids.includes(panel_id)) {
                new_opened_ids = [...new_opened_ids, panel_id];
            }
            if (device_id != null) {
                onUpdateOpenedIds(device_id, new_opened_ids);
            }
        };
        return (list_of_objects ?? [])
            .map((object, index) => [object, index])
            .sort(compareFn)
            .flatMap(([object, index]) => {
            if (objectFilter != null ? !objectFilter(object) : false) {
                return [];
            }
            const id = object.id;
            return {
                id: id.toString(),
                content: (setItInDroppableItemProps) => {
                    const active = opened_ids.includes(id);
                    const _validation_result = unpack_validation(validation_result, index);
                    const _validation_warnings_result = unpack_validation(validation_warnings_result, index);
                    return (_jsx(AccordionWithValidation, { validation_result: _validation_result, validation_warnings_result: _validation_warnings_result, header: _jsxs(Box, { display: "flex", alignItems: "center", justifyContent: "space-between", flexGrow: 1, children: [beforeTitleComponentRender &&
                                    beforeTitleComponentRender(object), _jsx(Box, { flexGrow: 1, children: _jsx(Typography, { variant: "subtitle1", children: nameGetter != null
                                            ? nameGetter(object, index)
                                            : // @ts-ignore
                                                object.name ?? '' }) }), titleComponentRender &&
                                    titleComponentRender(object), (showTrashButton?.(object) ?? true) && (_jsx(Box, { mr: 2, children: _jsx(IconButton, { onClick: onTrashClick(object), size: "small", children: _jsx(Delete, {}) }) }))] }), expanded: active, headerProps: {
                            onClick: onTitleClick(id),
                            onTouchStart: preventDefault,
                        }, ...setItInDroppableItemProps, children: active && (_jsx(_Fragment, { children: _jsx(Component, { id: object.id }, object.id) })) }));
                },
            };
        });
    }, [
        list_of_objects,
        Component,
        objectFilter,
        opened_ids,
        validation_warnings_result,
        validation_result,
        nameGetter,
        titleComponentRender,
        showTrashButton,
        onTrashClick,
        onUpdateOpenedIds,
        device_id,
        compareFn,
    ]);
    return (_jsxs(SettingCard, { title: _jsx(CardHeader, { sx: {
                flexWrap: 'wrap',
                alignItems: 'center',
            }, title: _jsxs(Box, { display: "flex", children: [_jsx(Typography, { variant: "h6", children: props.name }), help_id && (_jsx(MaterialHelpPopupUncontrolled, { help_id: help_id }))] }), action: !hideAddButton && (_jsx(Button, { variant: "outlined", color: "primary", startIcon: _jsx(Add, {}), size: "small", onClick: onClickAddButton, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C" })) }), children: [mainTitleComponentRender && !hideAddButton && panels.length == 0 && (_jsx(SettingFullRow, { children: mainTitleComponentRender() })), _jsx(SettingFullRow, { display: "flex", flexDirection: "column", gap: 0, children: panels.length > 0 &&
                    panels.map((panel) => (_jsx(React.Fragment, { children: panel.content(undefined) }, panel.id))) }), _jsx(MessageBox, { open: dialog_open, onClose: onClickCloseDialog, title: "\u041F\u0440\u0435\u0434\u0443\u043F\u0440\u0435\u0436\u0434\u0435\u043D\u0438\u0435", maxWidth: "xs", isWarning: true, footer: _jsxs(_Fragment, { children: [_jsx(Button, { color: "primary", onClick: onClickCloseDialog, children: "\u041E\u0442\u043C\u0435\u043D\u0430" }), _jsx(Button, { color: "error", onClick: onClickDeleteDialog, children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] }), children: msgs.map((msg, index) => (_jsxs(Box, { children: [index > 0 && _jsx(Divider, {}), _jsx(Typography, { variant: "subtitle1", color: "textSecondary", children: msg })] }, index))) })] }));
}
